import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Flex, Text, Tooltip } from "@radix-ui/themes";
import i18next from "i18next";
import { ParticipantsProps } from "./Participants.types";

/**
 * Participants component displays a list of participants with their avatars and names.
 * It also shows the count of additional participants if there are more than three.
 *
 * @component
 * @param {SingleProfile} lastThreeParticipantsProfiles - The properties for the Participants component.
 * @prop {number} participantsCount - The total number of participants.
 *
 * @returns {JSX.Element} The rendered Participants component.
 */

const Participants: FC<ParticipantsProps> = (props): JSX.Element => {
  // Variables
  const participantsCount = props.participantsCount;
  const participantCount = props.lastThreeParticipantsProfiles.length;
  const isFinished = props.status === "finished";

  return (
    <Box>
      <Flex gap="2" align="center">
        <Flex gap="1">
          {props.lastThreeParticipantsProfiles.map((profile, index) => {
            return (
              <Box key={`${profile?.userName}_${index}`}>
                <Tooltip content={profile.userName}>
                  <Avatar
                    src={profile.profileLogoUrl}
                    fallback={profile.userName.slice(0, 1)}
                    radius="full"
                    size="1"
                  />
                </Tooltip>
              </Box>
            );
          })}
        </Flex>
        <Flex>
          <Text as="p" size="2" color="gray">
            {props.lastThreeParticipantsProfiles.map((profile, index) => {
              return (
                <Text key={index}>
                  <Text>{profile.userName}</Text>
                  {index + 1 !== participantCount ? ", " : " "}
                </Text>
              );
            })}
            {participantCount < participantsCount - 3 && (
              <>
                <Text>
                  {i18next.t("challenge.participants.and")}{" "}
                  {/* {participantsCount - 3}{" "} */}
                  {i18next.t("challenge.participants.more")}{" "}
                </Text>
              </>
            )}
            {participantCount !== 0 && (
              <>
                {isFinished
                  ? i18next.t("challenge.participants.participated")
                  : i18next.t("challenge.participants.participating")}
                .
              </>
            )}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Participants;
